import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Scale, Subtract, Total } from "@pdfking/math"
import { ToPixel } from "@pdfking/quantity"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction, VerticalAlign } from "@pdfking/renderer-api"
import type { StringGUID } from "@pdfking/tfunc"

import { PALETTE, TITLE } from "#constants"
import type { Measure } from "#internal"

import { CreateRandom } from "../random/CreateRandom"
import { DrawProse } from "../text/DrawProse"

// The flap folded in from the front, in `box`: on the primary the title over
// a short secondary rule and a few lines about the book. `short` is the
// shortest side of the cover's boards, which it is set by
export const DrawFrontFlap = ({
  box,
  measure,
  seed,
  short,
}: {
  box: SpatialRectangle<Distance>
  measure: Measure
  seed: StringGUID
  short: Distance
}): PageShape[] => {
  const margin = Scale(short, 0.08)
  const font_size = Scale(short, 1 / 36)
  const line_height = Scale(font_size, 1.45)
  const title_size = Scale(font_size, 1.8)
  const top = Subtract(Total(box.y, box.height), margin)
  const rule_top = Subtract(top, Scale(font_size, 2.6))
  const rule = Scale(short, 0.004)
  const prose_top = Subtract(rule_top, line_height)
  const prose_height = Scale(Subtract(prose_top, Total(box.y, margin)), 0.6)
  const [prose] = DrawProse({
    box: {
      height: prose_height,
      width: Subtract(box.width, Scale(margin, 2)),
      x: Total(box.x, margin),
      y: Subtract(prose_top, prose_height),
    },
    color: PALETTE.text,
    font_size,
    line_height,
    measure,
    random: CreateRandom(seed, "front flap"),
  })
  return [
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.primary, position: box },
    {
      enum: PlotFunction.PLOT_TEXT,
      font_size: ToPixel(title_size),
      horizontal_align: HorizontalAlign.LEFT,
      position: {
        height: title_size,
        width: Subtract(box.width, Scale(margin, 2)),
        x: Total(box.x, margin),
        y: Subtract(top, title_size),
      },
      text: TITLE,
      text_color: PALETTE.text,
      vertical_align: VerticalAlign.TOP,
    },
    {
      enum: PlotFunction.PLOT_RECT,
      fill_color: PALETTE.secondary,
      position: {
        height: rule,
        width: Scale(short, 0.06),
        x: Total(box.x, margin),
        y: Subtract(rule_top, rule),
      },
    },
    ...prose,
  ]
}
